import { runDataIntegrityCheck } from "@/lib/dataIntegrity";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { DemoModeProvider } from "@/contexts/DemoModeContext";
import { ScreenshotModeProvider } from "@/contexts/ScreenshotModeContext";
import { ProtectedRoute, PaidRoute, AdminRoute, SuperAdminRoute } from "@/components/ProtectedRoute";
import { Suspense } from "react";
import Landing from "./pages/Landing";
import AppShell from "./components/AppShell";
import RouteErrorFallback from "./components/RouteErrorFallback";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { RequireCertification } from "./components/RequireCertification";

// Every page below is code-split so a first-time visitor on the landing page
// does not download the whole app (admin area, charts, editor) up front.
const Login = lazyWithReload(() => import("./pages/Login"));
const Signup = lazyWithReload(() => import("./pages/Signup"));
const ForgotPassword = lazyWithReload(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));
const MfaChallenge = lazyWithReload(() => import("./pages/MfaChallenge"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const Chapters = lazyWithReload(() => import("./pages/Chapters"));
const ExamMode = lazyWithReload(() => import("./pages/ExamMode"));
const PracticeMode = lazyWithReload(() => import("./pages/PracticeMode"));
const PracticeReview = lazyWithReload(() => import("./pages/PracticeReview"));
const MockExams = lazyWithReload(() => import("./pages/MockExams"));
const ExamHistory = lazyWithReload(() => import("./pages/ExamHistory"));
const PracticeHistory = lazyWithReload(() => import("./pages/PracticeHistory"));
const ExamResults = lazyWithReload(() => import("./pages/ExamResults"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const AdminCertifications = lazyWithReload(() => import("./pages/AdminCertifications"));
const CertificationWorkspace = lazyWithReload(() => import("./pages/CertificationWorkspace"));
const AdminChapters = lazyWithReload(() => import("./pages/AdminChapters"));
const AdminLearningObjectives = lazyWithReload(() => import("./pages/AdminLearningObjectives"));
const AdminQuestions = lazyWithReload(() => import("./pages/AdminQuestions"));
const AdminExamSets = lazyWithReload(() => import("./pages/AdminExamSets"));
const AdminUsers = lazyWithReload(() => import("./pages/AdminUsers"));
const AdminSubscriptions = lazyWithReload(() => import("./pages/AdminSubscriptions"));
const AdminDataManagement = lazyWithReload(() => import("./pages/AdminDataManagement"));
const AdminKeywords = lazyWithReload(() => import("./pages/AdminKeywords"));
const AdminAnnouncements = lazyWithReload(() => import("./pages/AdminAnnouncements"));
const AdminPages = lazyWithReload(() => import("./pages/AdminPages"));
const AdminQuality = lazyWithReload(() => import("./pages/AdminQuality"));
const AdminAuthEvents = lazyWithReload(() => import("./pages/AdminAuthEvents"));
const SitePage = lazyWithReload(() => import("./pages/SitePage"));
const Notifications = lazyWithReload(() => import("./pages/Notifications"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const KeywordPractice = lazyWithReload(() => import("./pages/KeywordPractice"));
const Insights = lazyWithReload(() => import("./pages/Insights"));
const InsightQuestionPreview = lazyWithReload(() => import("./pages/InsightQuestionPreview"));
const Pricing = lazyWithReload(() => import("./pages/Pricing"));
const BillingReturn = lazyWithReload(() => import("./pages/BillingReturn"));
const AuthCallback = lazyWithReload(() => import("./pages/AuthCallback"));
const TrialExam = lazyWithReload(() => import("./pages/TrialExam"));

// Run data integrity check on startup — prune orphaned records
runDataIntegrityCheck();

const queryClient = new QueryClient();

const router = createBrowserRouter([
  { path: "/", element: <Landing />, errorElement: <RouteErrorFallback /> },
  { path: "/about", element: <SitePage /> },
  { path: "/contact", element: <SitePage /> },
  { path: "/privacy", element: <SitePage /> },
  { path: "/terms", element: <SitePage /> },
  { path: "/login", element: <Login /> },
  { path: "/signup", element: <Signup /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/reset-password", element: <ResetPassword /> },
  { path: "/login/mfa", element: <MfaChallenge /> },
  { path: "/pricing", element: <Pricing /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: "/billing/return", element: <BillingReturn /> },
      { path: "/trial/exam", element: <TrialExam /> },
      {
        element: <PaidRoute />,
        children: [
          {
            element: <AppShell />,
            children: [
              { path: "/settings", element: <Settings /> },
              { path: "/notifications", element: <Notifications /> },
            ],
          },
          {
            element: <AppShell />,
            children: [
              {
                element: <RequireCertification />,
                children: [
                  { path: "/dashboard", element: <Dashboard /> },
                  { path: "/insights", element: <Insights /> },
                  { path: "/insights/question/:questionId", element: <InsightQuestionPreview /> },
                  { path: "/chapters", element: <Chapters /> },
                  { path: "/practice-results", element: <PracticeHistory /> },
                  { path: "/mock-exams", element: <MockExams /> },
                  { path: "/mock-results", element: <ExamHistory /> },
                ],
              },
            ],
          },
          {
            element: <RequireCertification mode="redirect" />,
            children: [
              { path: "/exam", element: <ExamMode /> },
              { path: "/exam-results", element: <ExamResults /> },
              { path: "/practice-review/:sessionId", element: <PracticeReview /> },
              { path: "/practice/:certificationId/:chapterId", element: <PracticeMode /> },
              { path: "/keyword-practice/:certificationId/:chapterId", element: <KeywordPractice /> },
            ],
          },
        ],

      },
    ],
  },
  {
    element: <AdminRoute />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "/admin/certifications", element: <AdminCertifications /> },
          { path: "/admin/certifications/:certId", element: <CertificationWorkspace /> },
          { path: "/admin/chapters", element: <AdminChapters /> },
          { path: "/admin/learning-objectives", element: <AdminLearningObjectives /> },
          { path: "/admin/keywords", element: <AdminKeywords /> },
          { path: "/admin/questions", element: <AdminQuestions /> },
          { path: "/admin/exam-sets", element: <AdminExamSets /> },
          { path: "/admin/announcements", element: <AdminAnnouncements /> },
          { path: "/admin/pages", element: <AdminPages /> },
          { path: "/admin/users", element: <AdminUsers /> },
          {
            element: <SuperAdminRoute />,
            children: [
              { path: "/admin/subscriptions", element: <AdminSubscriptions /> },
              { path: "/admin/data-management", element: <AdminDataManagement /> },
              { path: "/admin/quality", element: <AdminQuality /> },
              { path: "/admin/auth-events", element: <AdminAuthEvents /> },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
]);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <DemoModeProvider>
          <ScreenshotModeProvider>
            <Toaster />
            <Sonner />
            <Suspense
              fallback={
                <div className="min-h-screen flex items-center justify-center bg-background">
                  <div className="h-8 w-8 border-[3px] border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              }
            >
              <RouterProvider router={router} />
            </Suspense>
          </ScreenshotModeProvider>
        </DemoModeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
